import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';
import { API } from '../../App';
import LinkField from './LinkField';

// ERPNext-style "+ New" quick-entry modal. Fetches the minimal field set
// from /api/erp-meta/quick-entry/{doctype}, validates client-side, posts.
// Triggered globally via Ctrl/Cmd+N or from any list/page action button.
export default function QuickEntry({ open, doctype, onClose, onCreated }) {
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !doctype) return;
    let cancel = false;
    setLoading(true);
    axios.get(`${API}/erp-meta/quick-entry/${doctype}`)
      .then(r => {
        if (cancel) return;
        setSchema(r.data);
        const init = {};
        (r.data.fields || []).forEach(f => { if (f.default != null) init[f.name] = f.default; });
        setForm(init);
      })
      .catch(() => toast.error('Could not load form'))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [open, doctype]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/erp-meta/quick-entry/${doctype}`, form);
      toast.success(`${schema?.label} created`);
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  }, [doctype, form, schema, submitting, onCreated, onClose]);

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={onClose}>
      <div style={{ background: 'var(--erp-surface)', borderRadius: 8, width: 480, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--erp-border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Plus size={16} color="var(--erp-primary)" />
          <strong style={{ fontSize: 14 }}>New {schema?.label || doctype}</strong>
          <button className="erp-icon-btn" style={{ marginLeft: 'auto' }} onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--erp-text-muted)', padding: 20 }}>Loading…</div>
          ) : !schema ? (
            <div style={{ color: 'var(--erp-text-muted)' }}>Schema unavailable.</div>
          ) : (
            <form onSubmit={e => { e.preventDefault(); submit(); }}>
              <div style={{ display: 'grid', gap: 12 }}>
                {(schema.fields || []).map(f => (
                  <FieldInput key={f.name} field={f} value={form[f.name]} onChange={v => setForm(s => ({ ...s, [f.name]: v }))} />
                ))}
              </div>
              <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
                <button type="button" className="erp-btn" onClick={async () => { await submit(); }}>Save</button>
                <button type="submit" className="erp-btn primary" disabled={submitting} onClick={async (e) => {
                  e.preventDefault();
                  setSubmitting(true);
                  try {
                    const r = await axios.post(`${API}/erp-meta/quick-entry/${doctype}`, form);
                    onCreated?.(r.data);
                    onClose();
                    navigate(`/doctype/${doctype}/${r.data.id}`);
                  } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
                  finally { setSubmitting(false); }
                }}>Save & Edit</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const inputStyle = {
    width: '100%', padding: '6px 10px', fontSize: 13,
    border: '1px solid var(--erp-border)', borderRadius: 5,
    fontFamily: 'inherit',
  };
  const labelEl = (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--erp-text-muted)', marginBottom: 4 }}>
      {field.label}{field.required && <span style={{ color: 'var(--erp-danger)' }}> *</span>}
    </label>
  );
  let input;
  if (field.type === 'link' && field.options) {
    input = <LinkField value={value} onChange={onChange} doctype={field.options} placeholder={field.description} />;
  } else if (field.type === 'select' && field.options) {
    input = (
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle}>
        <option value="">—</option>
        {String(field.options).split('|').map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (field.type === 'check') {
    input = <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />;
  } else if (field.type === 'date') {
    input = <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle} />;
  } else if (field.type === 'currency' || field.type === 'float' || field.type === 'int') {
    input = <input type="number" step={field.type === 'int' ? '1' : 'any'} value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} style={inputStyle} />;
  } else if (field.type === 'text') {
    input = <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />;
  } else {
    input = <input type="text" value={value ?? ''} onChange={e => onChange(e.target.value)} style={inputStyle} placeholder={field.description || ''} />;
  }
  return <div>{labelEl}{input}</div>;
}
